import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom-latest';
import { useRetrieveMarket } from 'hooks/api/market';
import { useSearchOrganizationDomain } from 'hooks/api/organization-domain';
import { messages } from 'i18n';
import { DialogContent, DialogFooter, DialogHeader } from 'components/Dialog';
import { BaseRoutedDialogContentProps } from 'broker/components/RoutedDialog';
import FormSkeleton from 'broker/components/skeletons/FormSkeleton';
import FormContent from './FormContent';
import { CarrierFormDataProps } from './types';
import { FormMode, UrlParam } from './utils';

export default function Content({
  onClose,
  setDialogIsDirty,
  closeWithoutPrompt,
  locationState,
}: BaseRoutedDialogContentProps) {
  const [searchParams] = useSearchParams();

  const id = searchParams.get(UrlParam.Id) ?? '';

  const { data: market, isLoading } = useRetrieveMarket({ id, enabled: !!id });

  const { items: domains, isLoading: isDomainsLoading } = useSearchOrganizationDomain({
    filter: { organizationId: market?.organizationId },
    enabled: !!market?.organizationId,
  });

  const mode = id ? FormMode.Update : FormMode.Create;

  const title =
    mode === FormMode.Update
      ? messages.carriersPage.carrierForm.title.update(market?.organizationName ?? '')
      : messages.carriersPage.carrierForm.title.create;

  const defaultValues = useMemo(
    (): CarrierFormDataProps => ({
      name: market?.organizationName ?? '',
      domain: market?.organizationId ? domains.map((d) => d.domain) : [],
      purchasedModules: market?.purchasedModules ?? [],
      stpConfidenceThreshold: market?.stpConfidenceThreshold ?? null,
    }),
    [market, domains],
  );

  return (
    <>
      <DialogHeader id="carrier-dialog" onClose={onClose} title={title} />

      {!id || (market && !isLoading && !isDomainsLoading) ? (
        <FormContent
          existingDomains={domains}
          mode={mode}
          setDialogIsDirty={setDialogIsDirty}
          onClose={onClose}
          defaultValues={defaultValues}
          closeWithoutPrompt={closeWithoutPrompt}
          scrollTo={locationState?.scrollTo}
          market={market ?? null}
        />
      ) : (
        <>
          <DialogContent>
            <FormSkeleton />
          </DialogContent>
          <DialogFooter showSkeleton />
        </>
      )}
    </>
  );
}
